import React, { useState, useEffect, useCallback } from 'react';

// Reusable "choose a Google Drive folder" modal. Browse My Drive + shared drives,
// navigate into subfolders, create a new folder, and pick a destination.
// Props:
//   onCancel()              — close without choosing
//   onSelect({folderId, driveId, name, path})  — chosen destination
//   saveLabel               — label for the confirm button (default "Save here")
const API = process.env.REACT_APP_API_URL || '/api';
const FOLDER_MIME = 'application/vnd.google-apps.folder';
const NAVY = '#1A3A5C';
const ORANGE = '#EA6F2E';

export default function DriveFolderPicker({ onCancel, onSelect, saveLabel = 'Save here' }) {
  const [connected, setConnected] = useState(null);        // null=checking, true, false
  const [drives, setDrives] = useState([]);                // shared drives
  const [source, setSource] = useState({ type: 'my-drive', driveId: null, rootName: 'My Drive' });
  const [path, setPath] = useState([{ id: 'root', name: 'My Drive' }]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  const authHeader = useCallback(() => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }), []);
  const current = path[path.length - 1];

  // Connection check + load shared drives
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const s = await fetch(`${API}/google/status`, { headers: authHeader() }).then(r => r.json());
        if (cancel) return;
        setConnected(!!s.connected);
        if (s.connected) {
          const d = await fetch(`${API}/google/drive/drives`, { headers: authHeader() }).then(r => r.json());
          if (!cancel) setDrives(d.drives || []);
        }
      } catch { if (!cancel) setConnected(false); }
    })();
    return () => { cancel = true; };
  }, [authHeader]);

  // Load subfolders of the current folder
  const loadFolders = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      p.set('folderId', current.id);
      if (source.driveId) p.set('driveId', source.driveId);
      const data = await fetch(`${API}/google/drive/files?${p}`, { headers: authHeader() }).then(r => r.json());
      const onlyFolders = (data.files || []).filter(f => f.mimeType === FOLDER_MIME).map(f => ({ id: f.id, name: f.name }));
      setFolders(onlyFolders);
    } catch { setFolders([]); }
    finally { setLoading(false); }
  }, [current.id, source.driveId, authHeader]);

  useEffect(() => { if (connected === true) loadFolders(); }, [connected, loadFolders]);

  const switchSource = (type, driveId, name) => {
    if (type === 'my-drive') { setSource({ type, driveId: null, rootName: 'My Drive' }); setPath([{ id: 'root', name: 'My Drive' }]); }
    else { setSource({ type: 'shared-drive', driveId, rootName: name }); setPath([{ id: driveId, name }]); }
  };
  const enter = (folder) => setPath(prev => [...prev, folder]);
  const jump = (i) => setPath(prev => prev.slice(0, i + 1));

  const createFolder = async (e) => {
    if (e) e.preventDefault();
    const name = newName.trim();
    if (!name || busy) { setNewOpen(false); return; }
    setBusy(true);
    try {
      const body = { name, parentId: current.id };
      if (source.driveId) body.driveId = source.driveId;
      const created = await fetch(`${API}/google/drive/folder`, {
        method: 'POST', headers: { ...authHeader(), 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      }).then(r => r.json());
      await loadFolders();
      if (created && created.id) enter({ id: created.id, name: created.name || name }); // step into the new folder
    } catch { /* non-fatal */ }
    finally { setBusy(false); setNewOpen(false); setNewName(''); }
  };

  const connectDrive = async () => {
    try {
      const { url } = await fetch(`${API}/google/auth`, { headers: authHeader() }).then(r => r.json());
      if (!url) return;
      const popup = window.open(url, 'drive_connect', 'width=520,height=640');
      const onMsg = (ev) => {
        if (ev.data && ev.data.type === 'GMAIL_CONNECTED') {
          setConnected(true);
          window.removeEventListener('message', onMsg);
          if (popup && !popup.closed) popup.close();
        }
      };
      window.addEventListener('message', onMsg);
    } catch { /* ignore */ }
  };

  const confirm = () => onSelect({
    folderId: current.id, driveId: source.driveId, name: current.name, path: path.map(p => p.name).join(' / ')
  });

  return (
    <div style={S.overlay} onClick={onCancel}>
      <div style={S.card} onClick={e => e.stopPropagation()}>
        <div style={S.head}>
          <h3 style={S.title}>Choose a Google Drive folder</h3>
          <button style={S.x} onClick={onCancel}>✕</button>
        </div>

        {connected === null && <div style={S.muted}>Checking Drive connection…</div>}

        {connected === false && (
          <div style={{ padding: '24px 4px', textAlign: 'center' }}>
            <p style={{ ...S.muted, marginBottom: 14 }}>Connect your CAAM Google account to save into Drive.</p>
            <button style={S.primary} onClick={connectDrive}>Connect Google Drive</button>
          </div>
        )}

        {connected === true && (
          <>
            {/* Source chips: My Drive + shared drives */}
            <div style={S.sources}>
              <button style={{ ...S.chip, ...(source.type === 'my-drive' ? S.chipOn : {}) }} onClick={() => switchSource('my-drive')}>My Drive</button>
              {drives.map(d => (
                <button key={d.id} style={{ ...S.chip, ...(source.driveId === d.id ? S.chipOn : {}) }} onClick={() => switchSource('shared-drive', d.id, d.name)}>{d.name}</button>
              ))}
            </div>

            {/* Breadcrumbs */}
            <div style={S.crumbs}>
              {path.map((c, i) => (
                <React.Fragment key={c.id + i}>
                  {i > 0 && <span style={{ color: '#b6bcc6' }}>›</span>}
                  <button style={{ ...S.crumb, ...(i === path.length - 1 ? { color: NAVY, fontWeight: 700, cursor: 'default' } : {}) }}
                    onClick={() => i < path.length - 1 && jump(i)}>{c.name}</button>
                </React.Fragment>
              ))}
            </div>

            {/* Folder list */}
            <div style={S.list}>
              {loading && <div style={{ ...S.muted, padding: 12 }}>Loading…</div>}
              {!loading && folders.length === 0 && <div style={{ ...S.muted, padding: 12 }}>No sub-folders here. You can save into this folder or create a new one.</div>}
              {!loading && folders.map(f => (
                <button key={f.id} style={S.folderRow} onClick={() => enter(f)} title={`Open ${f.name}`}>
                  <span style={{ color: ORANGE, marginRight: 10 }}>📁</span>
                  <span style={S.folderName}>{f.name}</span>
                  <span style={{ color: '#c0c6cf' }}>›</span>
                </button>
              ))}
            </div>

            {/* Footer */}
            <div style={S.footer}>
              <button style={S.ghost} onClick={() => { setNewOpen(true); setNewName(''); }}>+ New folder</button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={S.cancel} onClick={onCancel}>Cancel</button>
                <button style={S.primary} onClick={confirm}>{saveLabel} <b>&ldquo;{current.name}&rdquo;</b></button>
              </div>
            </div>
          </>
        )}

        {newOpen && (
          <form onSubmit={createFolder} style={S.newInline} onClick={e => e.stopPropagation()}>
            <input autoFocus value={newName} onChange={e => setNewName(e.target.value)} placeholder="New folder name" style={S.input} disabled={busy} />
            <button type="submit" style={S.primary} disabled={busy}>{busy ? 'Creating…' : 'Create'}</button>
            <button type="button" style={S.cancel} onClick={() => setNewOpen(false)} disabled={busy}>Cancel</button>
          </form>
        )}
      </div>
    </div>
  );
}

const S = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 16 },
  card: { background: '#fff', borderRadius: 14, width: '100%', maxWidth: 560, boxShadow: '0 20px 60px rgba(0,0,0,0.3)', padding: 18, fontFamily: "'Roboto',-apple-system,sans-serif" },
  head: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 },
  title: { margin: 0, fontSize: 16.5, fontWeight: 700, color: NAVY },
  x: { border: 'none', background: 'none', fontSize: 16, color: '#8a8f98', cursor: 'pointer' },
  muted: { color: '#8a8f98', fontSize: 13 },
  sources: { display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 },
  chip: { border: '1px solid #e6e9ef', background: '#fff', color: '#33415c', borderRadius: 999, padding: '5px 12px', fontSize: 12.5, fontWeight: 500, cursor: 'pointer' },
  chipOn: { background: NAVY, color: '#fff', borderColor: NAVY },
  crumbs: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '6px 2px 10px', flexWrap: 'wrap' },
  crumb: { border: 'none', background: 'none', color: ORANGE, fontSize: 13, cursor: 'pointer', padding: 0 },
  list: { border: '1px solid #eef0f3', borderRadius: 10, maxHeight: 280, overflowY: 'auto', minHeight: 120 },
  folderRow: { display: 'flex', alignItems: 'center', width: '100%', padding: '10px 12px', background: '#fff', border: 'none', borderBottom: '1px solid #f4f5f7', cursor: 'pointer', textAlign: 'left', fontSize: 13.5, color: '#1f2937' },
  folderName: { flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  footer: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14 },
  ghost: { border: '1px solid #e6e9ef', background: '#fff', color: NAVY, borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  cancel: { border: '1px solid #e6e9ef', background: '#fff', color: NAVY, borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  primary: { border: 'none', background: ORANGE, color: '#fff', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  newInline: { display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' },
  input: { flex: 1, border: '1px solid #e6e9ef', borderRadius: 8, padding: '8px 11px', fontSize: 13, outline: 'none' },
};
